import { useRouter } from "next/router";

import { useSelector } from "react-redux";

import { ShoppingCartItem } from "modules/Cart/ShoppingCart.interface";

import { selectChainId, selectUserEmail } from "redux/slices/metamask";

import useLogger from "hooks/useLogger";

import { PAYMENT_TOKENS } from "enums/paymentTokens";
import { ACTION_TYPES } from "enums/logger/ActionTypes";
import { FIAT_REDEEM_STEPS, PATHS } from "enums/logger/Paths";
import { TRANSACTIONS_STATUSES } from "enums/transactionsStatuses";
import { PURPOSES_TYPES } from "enums/purposesTypes";

import {
  PaymentHandlerParams,
  PaymentHandlerResponse,
} from "interfaces/PaymentHandler.interface";

import { API_ROUTES, APP_ROUTES } from "config/routes";

import { captureException } from "utils/errors";

import fetcher from "lib/fetchJson";

const useFiatPayment = (
  purpose = PURPOSES_TYPES.PHYSICAL,
  items?: ShoppingCartItem[],
): ((
  params: PaymentHandlerParams,
) => Promise<PaymentHandlerResponse | null>) => {
  const { asPath } = useRouter();
  const chainId = useSelector(selectChainId);
  const userEmail = useSelector(selectUserEmail);
  const log = useLogger();

  return async ({
    price,
    formValues,
    captchaToken,
    currency,
  }: PaymentHandlerParams) => {
    try {
      const isFreeMint = price === 0;

      const getRedeemIds = async (fiatTransactionId = null) => {
        const { data } = await fetcher(
          `${API_ROUTES.ORDER_PAY}?chainId=${chainId}`,
          {
            items,
            ...(formValues ? formValues : {}),
            paymentToken: currency,
            purpose,
            price,
            fiatTransactionId,
            captchaToken,
          },
          "POST",
        );

        return data.claimingIds;
      };

      if (!isFreeMint) {
        const {
          data: { transactionId },
        } = await fetcher(
          API_ROUTES.FIAT_TRANSACTION,
          {
            price,
            currency,
            widgetBaseUrl: `${process.env.NEXT_PUBLIC_APP_URL}${APP_ROUTES.ORDER_PAYMENT_STATUS}`,
          },
          "POST",
        );
        await getRedeemIds(transactionId);

        const openAri10Widget = () => {
          window.dispatchEvent(
            new CustomEvent("ari10-widget-start-commodities-payment-request", {
              detail: {
                transactionId,
                buyCurrencyCode: PAYMENT_TOKENS.USDC,
                offerMoney: {
                  amount: price,
                  currencyCode: currency,
                },
                mailAddress: userEmail ?? "",
              },
            }),
          );
        };

        window.addEventListener("ari10-transaction-window-loaded-event", () => {
          openAri10Widget();
        });

        openAri10Widget();

        log({
          path: PATHS.FIAT_REDEEM,
          step: FIAT_REDEEM_STEPS.TRANSACTION_ID,
          data: { transactionId, pathname: asPath },
          txHash: transactionId,
          actionType: ACTION_TYPES.USER,
        });
      }

      if (isFreeMint) {
        const redeemIds = await getRedeemIds();
        await fetcher(
          `${API_ROUTES.REDDEM_MINT}?purpose=${purpose}`,
          redeemIds,
          "POST",
        );

        return {
          status: TRANSACTIONS_STATUSES.SUCCESS,
        };
      }

      return null;
    } catch (error) {
      captureException(error);
      log({
        path: PATHS.FIAT_REDEEM,
        step: FIAT_REDEEM_STEPS.TRANSACTION_ID,
        data: { pathname: asPath },
        error,
        actionType: ACTION_TYPES.USER,
      });
      return {
        status: TRANSACTIONS_STATUSES.ERROR,
        errorMsg: error.data?.message ?? "Something went wrong",
      };
    }
  };
};

export default useFiatPayment;
